/**
 * Registration Wizard - Phase 4 Implementation
 *
 * Main wizard component that orchestrates the registration workflow.
 * Replaces the monolithic RegisterAssetPage.tsx step management logic.
 */

import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Container,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import React, { useCallback } from 'react';
import { enhancedTaxonomyService } from '../../../services/enhancedTaxonomyService';
import { useRegistrationContext } from '../contexts/RegistrationContext';
import {
    useRegistrationFlow,
    useRegistrationForm,
} from '../hooks/useRegistrationFlow';
import { StepIndicator } from './StepIndicator';
import { StepNavigation } from './StepNavigation';
import { StepRenderer } from './StepRenderer';

export interface RegistrationWizardProps {
  /** Optional initial state for testing */
  initialState?: any;
  /** Callback when registration is completed */
  onComplete?: (asset: any) => void;
  /** Callback when registration is cancelled */
  onCancel?: () => void;
  /** Callback for "Create Another" on success (e.g. navigate to /register-asset). If not set, falls back to window.location.reload() */
  onRegisterAnother?: () => void;
}

/**
 * Main Registration Wizard Component
 *
 * Orchestrates the entire registration workflow using:
 * - RegistrationContext for state management
 * - Custom hooks for business logic
 * - Modular step components for UI
 */
export const RegistrationWizard: React.FC<RegistrationWizardProps> = ({
  initialState,
  onComplete,
  onCancel,
  onRegisterAnother,
}) => {
  const {
    state,
    dispatch,
    setSelectedLayer,
    advanceStep,
    setTaxonomySelection,
    resetUploadAndAIState,
    updateFormData,
    addUploadedFile,
    removeUploadedFile,
    setIsVariant,
    setBaseAsset,
    submitAsset
  } = useRegistrationContext();
  const { flowState, nextStep, previousStep } = useRegistrationFlow();
  const { formData, layerMetadata, hasUnsavedChanges } =
    useRegistrationForm();

  // Handle successful completion
  const handleComplete = useCallback(
    (createdAsset: any) => {
      if (onComplete) {
        onComplete(createdAsset);
      }
    },
    [onComplete]
  );

  // Handle cancellation with unsaved changes check
  const handleCancel = useCallback(() => {
    if (hasUnsavedChanges) {
      const shouldDiscard = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!shouldDiscard) return;
    }

    if (onCancel) {
      onCancel();
    }
  }, [hasUnsavedChanges, onCancel]);

  // When user clicks Next/Submit: on last step run submit, else advance step
  const handleNext = useCallback(async () => {
    if (flowState.isLastStep) {
      console.log('🚀 [REGISTRATION WIZARD] Submit triggered from navigation');
      try {
        let rawFiles = formData.files || [];
        let files = rawFiles.filter((f: any) => f instanceof File) as File[];
        // Fallback: when formData.files is empty but we have uploadedFiles (e.g. composite flow), try to create a File from the first uploaded URL
        if (files.length === 0 && state.uploadedFiles.length > 0) {
          console.log('⚠️ [REGISTRATION WIZARD] No files in formData, trying to use first uploaded file URL...');
          const first = state.uploadedFiles[0];
          const url = (first as any).url || (first as any).fileUrl;
          if (url) {
            try {
              const res = await fetch(url);
              const blob = await res.blob();
              const name = (first as any).originalName || (first as any).fileName || 'uploaded-video';
              const type = (first as any).mimeType || blob.type || 'video/mp4';
              files = [new File([blob], name, { type })];
            } catch (fetchErr) {
              console.warn('⚠️ [REGISTRATION WIZARD] Could not create File from uploaded URL:', fetchErr);
            }
          }
        }
        if (files.length === 0) {
          console.warn('⚠️ [REGISTRATION WIZARD] No files to submit - check file upload step');
          console.warn('⚠️ [REGISTRATION WIZARD] formData keys:', Object.keys(formData));
          return;
        }
        await submitAsset(files);
        console.log('✅ [REGISTRATION WIZARD] Asset submitted successfully');
        if (onComplete && (state as any).createdAsset) {
          onComplete((state as any).createdAsset);
        }
      } catch (error) {
        console.error('❌ [REGISTRATION WIZARD] Submit failed:', error);
      }
    } else {
      nextStep();
    }
  }, [flowState.isLastStep, formData, state.uploadedFiles, state, submitAsset, onComplete, nextStep]);

  // Show loading state
  if (state.isSubmitting) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Creating Asset...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we process your submission.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Show success state
  if (state.submissionSuccess && (state as any).createdAsset) {
    const createdAsset = (state as any).createdAsset;

    // CRITICAL FIX: Check variant status from createdAsset (server response), not formData
    // Server response has the authoritative data after asset creation
    const isVariant =
      createdAsset.assetType === 'variant' ||
      createdAsset.starMetadata?.assetType === 'variant' ||
      createdAsset.looksMetadata?.assetType === 'variant' ||
      createdAsset.songsMetadata?.assetType === 'variant' ||
      state.formData.assetType === 'variant';

    // Extract baseAssetId from various possible locations in server response
    const baseAssetId =
      createdAsset.baseStarId ||  // Root level (if backend adds it)
      createdAsset.starMetadata?.baseStarId ||  // Stars layer
      createdAsset.looksMetadata?.baseLookId ||  // Looks layer (future)
      createdAsset.songsMetadata?.baseSongId ||  // Songs layer (future)
      state.formData.baseAssetId;  // Fallback to formData

    console.log('✅ [SUCCESS PAGE] Asset created:', {
      assetId: createdAsset.id,
      isVariant,
      baseAssetId,
      createdAssetType: createdAsset.assetType,
      starMetadataType: createdAsset.starMetadata?.assetType,
      formDataType: state.formData.assetType
    });

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 4 }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">
                {isVariant ? 'Variant Asset Created Successfully!' : 'Asset Created Successfully!'}
              </Typography>
              {isVariant && (
                <Chip
                  label="Variant"
                  color="secondary"
                  size="small"
                  sx={{
                    fontWeight: 600,
                    bgcolor: '#9C27B0',
                    color: 'white'
                  }}
                />
              )}
            </Box>
            <Typography variant="body2">
              Your {state.selectedLayer} layer asset has been created with ID:{' '}
              <strong>{createdAsset.id || createdAsset.friendlyName || createdAsset.name}</strong>
            </Typography>
            {isVariant && baseAssetId && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Base Asset: <strong>{baseAssetId}</strong>
              </Typography>
            )}
            {!isVariant && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                Your asset has been registered and is now available in the registry.
              </Typography>
            )}
          </Alert>

          <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleComplete(createdAsset)}
            >
              View Asset
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => (onRegisterAnother ? onRegisterAnother() : window.location.reload())}
            >
              Create Another
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Paper elevation={2} sx={{ p: 3, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Typography variant="h5" gutterBottom>
          Register Asset
        </Typography>
        {/* Step Indicator */}
        <StepIndicator
          currentStep={flowState.currentStep}
          totalSteps={flowState.totalSteps}
          stepName={flowState.stepName}
          stepDescription={flowState.stepDescription}
          progress={flowState.stepProgress}
          selectedLayer={state.selectedLayer}
        />

        {/* Error Display */}
        {state.submissionError && (
          <Alert severity="error" sx={{ m: 2 }}>
            {state.submissionError}
          </Alert>
        )}

        {/* AI Processing Error */}
        {state.aiError && (
          <Alert severity="warning" sx={{ m: 2 }}>
            AI Processing Error: {state.aiError}
          </Alert>
        )}

        {/* Main Content Area */}
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
          <StepRenderer
            currentStep={flowState.currentStep}
            selectedLayer={state.selectedLayer}
            taxonomySelection={state.taxonomySelection}
            layerMetadata={layerMetadata}
            uploadedFiles={state.uploadedFiles}
            selectedComponents={state.selectedComponents}
            isSubmitting={state.isSubmitting}
            formData={formData}
            updateFormData={updateFormData}
            aiResults={state.aiResults}
            // Legacy context actions (for backwards compatibility)
            setSelectedLayer={setSelectedLayer}
            setTaxonomySelection={setTaxonomySelection}
            addUploadedFile={addUploadedFile}
            removeUploadedFile={removeUploadedFile}
            setIsVariant={setIsVariant}
            setBaseAsset={setBaseAsset}
            // New callback system
            onLayerSelect={(layer: any, isDoubleClick?: boolean) => {
              console.log(`🎯 [REGISTRATION WIZARD] Layer selected:`, { layer: layer.code, isDoubleClick, currentState: state.selectedLayer });
              setSelectedLayer(layer.code);
              if (isDoubleClick) {
                console.log(`🎯 [REGISTRATION WIZARD] Double-click detected, advancing step`);
                advanceStep();
              }
            }}
            onTaxonomyUpdate={async (field: string, value: any) => {
              if (!state.selectedLayer) return;

              if (field === 'categoryCode') {
                const categoryCode = String(value || '').trim();
                let categoryName = categoryCode;

                if (categoryCode) {
                  try {
                    const categories = await enhancedTaxonomyService.getCategories(state.selectedLayer);
                    categoryName = categories.find(cat => cat.code === categoryCode)?.name || categoryCode;
                  } catch (error) {
                    console.error('Failed loading category name', error);
                  }
                }

                resetUploadAndAIState();
                updateFormData({
                  categoryCode,
                  categoryName,
                  subcategoryCode: '',
                  subcategoryName: '',
                  subcategoryNumericCode: '',
                });
                setTaxonomySelection({
                  layer: state.selectedLayer,
                  category: categoryCode,
                  subcategory: '',
                  categoryName,
                  subcategoryName: '',
                });
                return;
              }

              if (field === 'subcategoryCode') {
                const currentCategory = String(
                  state.taxonomySelection?.category || state.formData?.categoryCode || ''
                ).trim();
                if (!currentCategory) {
                  updateFormData({
                    subcategoryCode: '',
                    subcategoryName: '',
                    subcategoryNumericCode: '',
                  });
                  return;
                }

                const subcategoryCode = String(value || '').trim();
                let subcategoryName = subcategoryCode;
                let subcategoryNumericCode = '';

                if (subcategoryCode) {
                  try {
                    const subcategories = await enhancedTaxonomyService.getSubcategories(
                      state.selectedLayer,
                      currentCategory
                    );
                    const subcategory = subcategories.find(subcat => subcat.code === subcategoryCode);
                    subcategoryName = subcategory?.name || subcategoryCode;
                    subcategoryNumericCode = String(subcategory?.numericCode || '');
                  } catch (error) {
                    console.error('Failed loading subcategory name', error);
                  }
                }

                resetUploadAndAIState();
                updateFormData({
                  subcategoryCode,
                  subcategoryName,
                  subcategoryNumericCode,
                });
                setTaxonomySelection({
                  layer: state.selectedLayer,
                  category: currentCategory,
                  subcategory: subcategoryCode,
                  categoryName: state.taxonomySelection?.categoryName || state.formData?.categoryName || currentCategory,
                  subcategoryName,
                });
                return;
              }

              updateFormData({ [field]: value });
            }}
            onSubcategorySelect={async (subcategoryCode: string) => {
              const selectedLayer = state.selectedLayer;
              const currentCategory = String(
                state.taxonomySelection?.category || state.formData?.categoryCode || ''
              ).trim();
              if (!selectedLayer || !currentCategory) {
                console.warn('Ignoring subcategory selection without category');
                return;
              }
              await (async () => {
                let resolvedName = subcategoryCode;
                let resolvedNumericCode = '';
                try {
                  const subcategories = await enhancedTaxonomyService.getSubcategories(
                    selectedLayer,
                    currentCategory
                  );
                  const subcategory = subcategories.find(subcat => subcat.code === subcategoryCode);
                  resolvedName = subcategory?.name || subcategoryCode;
                  resolvedNumericCode = String(subcategory?.numericCode || '');
                } catch (error) {
                  console.error('Failed loading subcategory in onSubcategorySelect', error);
                }
                resetUploadAndAIState();
                updateFormData({
                  subcategoryCode,
                  subcategoryName: resolvedName,
                  subcategoryNumericCode: resolvedNumericCode,
                });
                setTaxonomySelection({
                  layer: selectedLayer,
                  category: currentCategory,
                  subcategory: subcategoryCode,
                  categoryName: state.taxonomySelection?.categoryName || state.formData?.categoryName || currentCategory,
                  subcategoryName: resolvedName,
                });
              })();
            }}
            // AI processing callbacks to properly set metadata in context
            onAIResults={(results: any) => {
              console.log('🤖 [AI RESULTS] Setting AI results in context:', results);
              dispatch({ type: 'SET_AI_RESULTS', payload: results });
            }}
            onLayerMetadata={(metadata: any) => {
              console.log('🎯 [LAYER METADATA] Setting layer metadata in context:', metadata);
              dispatch({ type: 'SET_LAYER_METADATA', payload: metadata });
            }}
            onSubmit={async () => {
              console.log('🚀 [REGISTRATION WIZARD] Submit triggered');
              try {
                // Get files from formData (direct file access for one-step workflow)
                let rawFiles = formData.files || [];
                let files = rawFiles.filter((f: any) => f instanceof File) as File[];

                // FALLBACK: If no files in formData, try to get from uploadedFiles context
                if (files.length === 0 && state.uploadedFiles.length > 0) {
                  console.log('⚠️ [REGISTRATION WIZARD] No files in formData, checking uploadedFiles context...');
                  // Try to reconstruct File objects from uploadedFiles if possible
                  // Note: uploadedFiles contains FileUploadResponse objects, not File objects
                  // We need the actual File objects, so this is a fallback attempt
                  console.log('📋 [REGISTRATION WIZARD] uploadedFiles available:', state.uploadedFiles.length);
                  // For now, we'll rely on formData.files being set correctly
                }

                console.log('🚀 [REGISTRATION WIZARD] Files to submit:', files.length);
                console.log('🔍 [REGISTRATION WIZARD] formData.files:', formData.files);
                console.log('🔍 [REGISTRATION WIZARD] state.uploadedFiles:', state.uploadedFiles.length);

                if (files.length === 0) {
                  console.warn('⚠️ [REGISTRATION WIZARD] No files to submit - check file upload step');
                  console.warn('⚠️ [REGISTRATION WIZARD] formData keys:', Object.keys(formData));
                  return;
                }

                await submitAsset(files);
                console.log('✅ [REGISTRATION WIZARD] Asset submitted successfully');

                // Notify parent of completion if callback provided
                if (onComplete && (state as any).createdAsset) {
                  onComplete((state as any).createdAsset);
                }
              } catch (error) {
                console.error('❌ [REGISTRATION WIZARD] Submit failed:', error);
              }
            }}
          />
        </Box>

        {/* Navigation */}
        <StepNavigation
          flowState={flowState}
          onNext={handleNext}
          onPrevious={previousStep}
          onCancel={handleCancel}
          isSubmitting={state.isSubmitting}
          hasUnsavedChanges={hasUnsavedChanges}
        />
      </Paper>
    </Box>
  );
};

export default RegistrationWizard;
